/*
 * Lets users safely replace a Box folder's exclusion lists and default locale without
 * background polling overwriting edits that are already in progress. Stored pathname
 * regexes are shown as globs where possible and converted back only when saving.
 */
import React, { useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import { useTranslation } from 'src/arb-wrapper';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { BoxFolderDefaultLocaleSelect } from 'src/components/box-folders/BoxFolderDefaultLocaleSelect';
import {
  BoxFolderExclusionsEditor,
  EXCLUSIONS_MODAL_WIDTH,
} from 'src/components/box-folders/BoxFolderExclusionsEditor';
import { BOX_DEFAULT_PATHNAME_EXCLUSIONS, type SyncTargetLocale } from 'src/constants';
import { useExclusionListDraft } from 'src/hooks/useExclusionListDraft';
import { usePathnameListDraft } from 'src/hooks/usePathnameListDraft';
import { useUpdateSyncTargetMutation } from 'src/store/bender-api';
import { type SyncTargetDto, type UpdateSyncTargetRequest } from 'src/types/sync-target.types';
import { normalizeBenderError } from 'src/utils/benderErrors';
import { toEditableEntries, toExcludedPathnames, toPathnameEntries } from 'src/utils/exclusionEntries';
import { validateFolderIdEntry } from 'src/utils/exclusionValidation';
import { Logger } from 'src/utils/logger';

interface EditBoxFolderExclusionsModalProps {
  open: boolean;
  syncTarget: SyncTargetDto;
  onClose: () => void;
}

interface OpenEditorProps {
  syncTarget: SyncTargetDto;
  onClose: () => void;
}

const OpenEditor: React.FC<OpenEditorProps> = ({ syncTarget, onClose }) => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const [updateSyncTarget, { isLoading: isSaving }] = useUpdateSyncTargetMutation();

  const folderIds = useExclusionListDraft(
    validateFolderIdEntry,
    toEditableEntries(syncTarget.excludedFolderIds ?? []),
  );
  const pathnames = usePathnameListDraft(toPathnameEntries(syncTarget.excludedPathnames ?? []));
  const [storedLocale] = useState<SyncTargetLocale | null>(syncTarget.defaultLocale ?? null);
  const [defaultLocale, setDefaultLocale] = useState<SyncTargetLocale | null>(storedLocale);
  const [saveError, setSaveError] = useState<'missing' | 'retryable' | null>(null);

  const close = () => {
    if (isSaving) return;
    onClose();
  };

  // An unchanged locale is omitted so saving exclusions never overwrites a default set elsewhere meanwhile.
  const buildUpdateRequest = (): UpdateSyncTargetRequest => ({
    syncTargetId: syncTarget.syncTargetId,
    excludedFolderIds: [...folderIds.values],
    excludedPathnames: toExcludedPathnames(pathnames.entries),
    ...(defaultLocale !== storedLocale ? { defaultLocale } : {}),
  });

  const save = async () => {
    if (isSaving || saveError === 'missing') return;
    setSaveError(null);

    try {
      await updateSyncTarget(buildUpdateRequest()).unwrap();
      createToast({
        type: AlertType.SUCCESS,
        message: t('box_folder_exclusions_saved_next_scan'),
      });
      onClose();
    } catch (error) {
      const normalized = normalizeBenderError(error);
      Logger.error(`Failed to update exclusions for ${syncTarget.syncTargetId}`, {
        status: normalized.status,
        detail: normalized.message,
      });
      setSaveError(normalized.status === 404 ? 'missing' : 'retryable');
    }
  };

  const isMissing = saveError === 'missing';
  const isEditorDisabled = isSaving;

  return (
    <Modal
      bodySpacingInset="none"
      data-testid="edit-box-folder-exclusions-modal"
      open
      title={t('box_folder_exclusions_edit_title', { folderId: syncTarget.targetId })}
      width={EXCLUSIONS_MODAL_WIDTH}
      onClose={close}
    >
      <Column spacing="450" spacingInset="500">
        {saveError !== null && (
          <Alert
            data-testid="edit-box-folder-exclusions-alert"
            size="small"
            type={isMissing ? 'warning' : 'error'}
          >
            {t(isMissing
              ? 'box_folder_exclusions_no_longer_enrolled'
              : 'box_folder_exclusions_save_retry')}
          </Alert>
        )}

        <BoxFolderDefaultLocaleSelect
          isDisabled={isEditorDisabled}
          testIdPrefix="edit-box-folder"
          value={defaultLocale}
          onChange={setDefaultLocale}
        />

        {/* Not seeded with the defaults; stored entries that match them are only grouped like on Add. */}
        <BoxFolderExclusionsEditor
          defaultPathnames={BOX_DEFAULT_PATHNAME_EXCLUSIONS}
          folderIds={folderIds}
          isDisabled={isEditorDisabled}
          pathnames={pathnames}
          testIdPrefix="edit-box-folder"
        />
      </Column>

      <ModalFooter>
        <Row alignmentHorizontal="end" spacing="200">
          <Button
            data-testid="edit-box-folder-exclusions-cancel-button"
            disabled={isSaving}
            type="tertiary"
            onClick={close}
          >
            {t('form_global_cancel')}
          </Button>
          <Button
            data-testid="edit-box-folder-exclusions-save-button"
            disabled={isSaving || isMissing}
            type="primary"
            onClick={() => { void save(); }}
          >
            {isSaving
              ? <Loader data-testid="edit-box-folder-exclusions-saving" size="small" />
              : t('form_global_save')}
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

/**
 * Mounting the stateful editor only while open snapshots the selected row once.
 * Polling may replace `syncTarget` while mounted, but useState does not reseed.
 */
export const EditBoxFolderExclusionsModal: React.FC<EditBoxFolderExclusionsModalProps> = (props) => {
  if (!props.open) return null;
  return <OpenEditor syncTarget={props.syncTarget} onClose={props.onClose} />;
};
